import "./index.css";
import type { ElementType, MouseEvent } from "react";
import { useCallback } from "react";
import { clsx } from "clsx";
import { Spinner } from "../Spinner";
import type { ISpinnerColor, ISpinnerSize } from "../Spinner/types";
import { Typography } from "../Typography";
import { ButtonIcon } from "./ButtonIcon";
import type {
  ButtonBaseProps,
  IButtonColor,
  IButtonSize,
  IButtonSpinnerPosition,
  IButtonVariant,
} from "./types";
import type { ITypographyVariant } from "@/ui-kit";
import { useMediaQuery } from "../../hooks/useMediaQuery";

const getSpinnerColor = (
  variant: IButtonVariant,
  color: IButtonColor,
): ISpinnerColor => {
  if (variant === "filled") {
    if (color === "white") return "dark";
    return "light";
  }
  if (color === "primary") return "primary";
  if (color === "white") return "light";
  if (color === "layer1") return "light";
  if (color === "layer2") return "light";
  if (color === "layer3") return "light";
  if (color === "success") return "success";
  if (color === "warning") return "warning";
  if (color === "danger") return "danger";

  return "primary";
};

const textSize: Record<IButtonSize, ITypographyVariant> = {
  xs: "button-sm",
  sm: "button-sm",
  md: "button-md",
  lg: "button-md",
};

export const BaseButton = <C extends ElementType = "button">(
  props: ButtonBaseProps & { as?: C },
) => {
  const {
    // Custom props (not passed to element)
    as,
    loading,
    spinnerPosition: spinnerPositionProp,
    variant,
    color,
    content,
    size,
    startIcon,
    endIcon,
    allowClickOnLoading,
    isIcon: isIconProp,
    spinnerProps,
    // Props we need to reference
    children,
    className,
    type = "button",
    onClick,
    disabled,
    // Rest goes to element
    ...restProps
  } = props;

  const Component = as || "button";

  const isMobile = useMediaQuery("(max-width: 639px)");

  const buttonVariant: IButtonVariant = variant || "filled";
  const buttonColor: IButtonColor = color || "primary";
  const buttonSize: IButtonSize = size || (isMobile ? "lg" : "md");

  const isIcon = !!isIconProp;
  const spinnerDefaultSize: ISpinnerSize = buttonSize === "xs" ? "xs" : "sm";
  const spinnerPosition: IButtonSpinnerPosition = isIcon
    ? "center"
    : spinnerPositionProp || "center";

  const showStartIcon: boolean =
    !isIcon &&
    startIcon !== undefined &&
    !(spinnerPosition === "start" && loading === true);
  const showEndIcon: boolean =
    !isIcon &&
    endIcon !== undefined &&
    !(spinnerPosition === "end" && loading === true);
  const showSpinner: boolean = loading === true && !disabled;

  const spinnerColor = getSpinnerColor(buttonVariant, buttonColor);

  const rootClass: string = clsx(
    "nuco-button",
    `nuco-button--${buttonVariant}`,
    `nuco-button--${buttonColor}`,
    `nuco-button--${buttonSize}`,
    `nuco-button--spinner-${spinnerPosition}`,
    isIcon && "nuco-button--icon",
    loading && "nuco-button--loading",
    disabled && "nuco-button--disabled",
    className,
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!allowClickOnLoading && loading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (onClick) {
        onClick(e);
      }
    },
    [onClick, allowClickOnLoading, loading],
  );

  const componentProps =
    Component === "button" ? { type, disabled, ...restProps } : restProps;

  const Tag = Component as ElementType;

  return (
    <Tag
      {...componentProps}
      className={rootClass}
      onClick={handleClick}
      aria-busy={loading}
      aria-disabled={disabled}
    >
      {showSpinner && spinnerPosition === "start" && (
        <Spinner
          className="nuco-button__spinner"
          color={spinnerColor}
          size={spinnerDefaultSize}
          {...spinnerProps}
        />
      )}
      {showStartIcon && (
        <div className="nuco-button__icon">
          <ButtonIcon icon={startIcon} size={buttonSize} />
        </div>
      )}
      <Typography
        weight="bold"
        variant={textSize[buttonSize]}
        className="nuco-button__text"
      >
        {children || content}
      </Typography>
      {showEndIcon && (
        <div className="nuco-button__icon">
          <ButtonIcon icon={endIcon} size={buttonSize} />
        </div>
      )}
      {showSpinner && spinnerPosition === "center" && (
        <Spinner
          className="nuco-button__spinner"
          color={spinnerColor}
          size={spinnerDefaultSize}
          {...spinnerProps}
        />
      )}
      {showSpinner && spinnerPosition === "end" && (
        <Spinner
          className="nuco-button__spinner"
          color={spinnerColor}
          size={spinnerDefaultSize}
          {...spinnerProps}
        />
      )}
    </Tag>
  );
};
